import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "./AuthContext";
import AddToPlaylistModal from "../components/AddToPlaylistModal";

/**
 * Playlists personnalisées du fan connecté.
 * - `playlists` : mes playlists (résumé + track_ids), chargées une fois connecté
 * - `openPicker(track)` : ouvre la fenêtre « Ajouter à une playlist » depuis n'importe où
 * - `create`, `addTrack`, `removeTrack`, `refresh`
 */
const PlaylistsContext = createContext(null);

export function PlaylistsProvider({ children }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [playlists, setPlaylists] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [pickerTrack, setPickerTrack] = useState(null);

    const refresh = useCallback(async () => {
        if (!user) { setPlaylists([]); setLoaded(false); return; }
        try {
            const { data } = await api.get("/me/playlists");
            setPlaylists(Array.isArray(data) ? data : []);
        } catch { /* hors ligne : on garde la liste actuelle */ }
        setLoaded(true);
    }, [user]);

    useEffect(() => { refresh(); }, [refresh]);

    const upsert = (pl) => setPlaylists((list) => {
        const rest = list.filter((p) => p.id !== pl.id);
        return [pl, ...rest];
    });

    const create = async (title, trackId, opts = {}) => {
        try {
            const { data } = await api.post("/me/playlists", { title, track_id: trackId || null, is_public: !!opts.isPublic });
            upsert(data);
            return data;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            return null;
        }
    };

    const addTrack = async (playlistId, track) => {
        try {
            const { data } = await api.post(`/me/playlists/${playlistId}/tracks`, { track_id: track.id });
            upsert(data);
            if (data.added) toast.success(`Ajouté à « ${data.title} »`);
            else toast.info(`Déjà dans « ${data.title} »`);
            return data;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            return null;
        }
    };

    const removeTrack = async (playlistId, trackId) => {
        try {
            const { data } = await api.delete(`/me/playlists/${playlistId}/tracks/${trackId}`);
            upsert(data);
            return data;
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
            return null;
        }
    };

    const forget = (playlistId) => setPlaylists((list) => list.filter((p) => p.id !== playlistId));

    const openPicker = (track) => {
        if (!track) return;
        if (!user) {
            toast.info("Connecte-toi pour créer tes playlists");
            nav(`/login?next=${encodeURIComponent(loc.pathname)}`);
            return;
        }
        setPickerTrack(track);
    };

    return (
        <PlaylistsContext.Provider value={{ playlists, loaded, refresh, create, addTrack, removeTrack, forget, upsert, openPicker }}>
            {children}
            <AddToPlaylistModal
                track={pickerTrack}
                playlists={playlists}
                loaded={loaded}
                onCreate={create}
                onAdd={addTrack}
                onRemove={removeTrack}
                onClose={() => setPickerTrack(null)}
            />
        </PlaylistsContext.Provider>
    );
}

export const usePlaylists = () => useContext(PlaylistsContext) || {
    playlists: [], loaded: false, refresh: () => {}, create: async () => null, addTrack: async () => null,
    removeTrack: async () => null, forget: () => {}, upsert: () => {}, openPicker: () => {},
};
